import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { TreePersonCard } from '@/components/person/TreePersonCard';
import { PetCard } from '@/components/pet/PetCard';
import { useHybridPedigreeStore } from '@/hooks/useStores';
import type { PetResponse } from 'we-tree-api-client/v2';

interface ChildrenGenerationProps {
  familyId?: string;
  onTreeViewClick?: (familyMemberId: string) => void;
  onProfileClick?: (familyMemberId: string) => void;
  onAddClick?: (familyMemberId: string, option: 'child' | 'spouse' | 'parent' | 'pet') => void;
  canAddByMemberId?: Record<string, boolean>;
  showPets?: boolean;
  rootPets?: PetResponse[];
  rootPetsLoading?: boolean;
  onPetProfileClick?: (familyMemberId: string) => void;
}

const ChildrenGeneration: React.FC<ChildrenGenerationProps> = observer(
  ({
    familyId,
    onTreeViewClick,
    onProfileClick,
    onAddClick,
    canAddByMemberId = {},
    showPets = false,
    rootPets = [],
    rootPetsLoading = false,
    onPetProfileClick
  }) => {
    const { t } = useTranslation('pedigree');
    const hybridPedigreeStore = useHybridPedigreeStore();
    const { childrenFamilyMemberIds } = hybridPedigreeStore.childrenGen;

    const hasChildren = childrenFamilyMemberIds.length > 0;
    const hasPets = showPets && rootPets.length > 0;
    const showPetsSection = showPets && (rootPetsLoading || hasPets);

    if (!hasChildren && !showPetsSection) {
      return null;
    }

    return (
      <div className="generation generation-children">
        <span className="generation-label" aria-hidden>
          {t('generations.children')}
        </span>
        <div className="generation-children__content">
          {childrenFamilyMemberIds.map((childFamilyMemberId) => (
            <TreePersonCard
              key={childFamilyMemberId}
              familyMemberId={childFamilyMemberId}
              generation={1}
              onTreeViewClick={onTreeViewClick}
              onProfileClick={onProfileClick}
              onAddClick={onAddClick}
              canAddFamilyMember={canAddByMemberId[childFamilyMemberId] ?? false}
              isTreeAnchor={false}
            />
          ))}
          {showPets &&
            rootPets.map((pet) => (
              <PetCard key={pet.familyMemberId} pet={pet} familyId={familyId} onProfileClick={onPetProfileClick} />
            ))}
          {showPets && rootPetsLoading && rootPets.length === 0 && (
            <span className="generation-children__loading" aria-hidden>
              {t('loadingPets')}
            </span>
          )}
        </div>
      </div>
    );
  }
);

export default ChildrenGeneration;
